import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import LogoutSuccessModal from "@/components/modals/LogoutSuccessModal";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
    Menu,
    X,
    Mail,
    Phone,
    User,
    LogIn,
    UserPlus,
    Settings,
    LogOut,
    MessageCircle,
} from "lucide-react";

interface NavItem {
    label: string;
    href: string;
}

const navigation: NavItem[] = [
    {
        label: "Início",
        href: "/"
    },
    {
        label: "Serviços",
        href: "/servicos"
    },
    {
        label: "Portfólio",
        href: "/portfolio"
    },
    {
        label: "Sobre",
        href: "/sobre"
    },
    {
        label: "Contato",
        href: "/contato"
    }
];

const SaaSHeader = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { isAuthenticated, isAdmin, logout } = useAuth();
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const navLinkClass = ({ isActive }: { isActive: boolean }) =>
        `relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg hover:scale-105 hover:shadow-sm ${isActive
            ? "text-accent-foreground bg-accent shadow-sm border border-accent/30"
            : "text-muted-foreground hover:text-accent-foreground hover:bg-accent hover:border hover:border-accent/30"
        }`;

    const mobileNavClass = ({ isActive }: { isActive: boolean }) =>
        `flex items-center w-full px-4 py-3 text-base font-medium transition-colors rounded-lg ${isActive
            ? "text-primary bg-primary/10"
            : "text-muted-foreground hover:text-primary hover:bg-primary/5"
        }`;

    return (
        <>
        <header
            className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled
                ? "bg-background/95 backdrop-blur-lg border-b shadow-sm"
                : "bg-background/80 backdrop-blur-md border-b border-border/40"
                }`}
        >
            <div className="container-page">
                <div className="flex h-16 items-center">
                    {/* Logo - Esquerda */}
                    <div className="w-1/4">
                        <Link
                            to="/"
                            className="flex items-center hover:scale-105 transition-transform duration-200"
                        >
                            <img
                                src="https://ik.imagekit.io/AlphaLabs/logo.webp?tr=w-180,q-90,f-webp"
                                alt="AlphaLabs Logo"
                                className="h-16 w-16 object-contain drop-shadow-2x1 shadow-slate-600/50 filter brightness-125 contrast-150 saturate-150"
                            />
                            <span className="font-bold text-2xl tracking-tight bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent drop-shadow-lg">
                                AlphaLabs
                            </span>
                        </Link>
                    </div>

                    {/* Navigation - Centro */}
                    <div className="w-1/2 flex justify-center">
                        <nav className="hidden lg:flex items-center gap-2" aria-label="Principal">
                            {navigation.map((item) => (
                                <NavLink
                                    key={item.href}
                                    to={item.href}
                                    className={navLinkClass}
                                    end={item.href === "/"}
                                >
                                    {item.label}
                                </NavLink>
                            ))}
                        </nav>
                    </div>

                    {/* Actions - Direita */}
                    <div className="w-1/4 flex items-center justify-end gap-2">
                        {/* Contact Info (Desktop only) */}
                        <div className="hidden xl:flex items-center mr-2">
                            <a
                                href="https://wa.me/5573988188407"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-muted-foreground hover:text-accent-foreground hover:bg-accent hover:scale-105 transition-all duration-300 px-3 py-2 rounded-lg hover:border hover:border-accent/30 whitespace-nowrap"
                            >
                                <Phone className="w-4 h-4" />
                                <MessageCircle className="w-4 h-4" />
                                <span className="text-sm">(73) 98818-8407</span>
                            </a>
                        </div>

                        <Button
                            asChild
                            variant="outline"
                            size="sm"
                            className="hidden sm:inline-flex hover:scale-105 transition-all duration-300 bg-accent/50 text-accent-foreground border-accent/30 hover:bg-accent hover:text-accent-foreground hover:border-accent/50"
                        >
                            <Link to="/contato">
                                <Mail className="w-4 h-4 mr-2" />
                                Orçamento
                            </Link>
                        </Button>

                        {/* User Menu */}
                        {isAuthenticated ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="hover:scale-105 hover:bg-primary/10 transition-all duration-300"
                                    >
                                        <User className="w-4 h-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <DropdownMenuItem asChild>
                                        <Link to="/client" className="flex items-center">
                                            <User className="w-4 h-4 mr-2" />
                                            Portal do Cliente
                                        </Link>
                                    </DropdownMenuItem>

                                    {isAdmin && (
                                        <>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem asChild>
                                                <Link to="/admin" className="flex items-center text-primary">
                                                    <Settings className="w-4 h-4 mr-2" />
                                                    Painel Admin
                                                </Link>
                                            </DropdownMenuItem>
                                        </>
                                    )}

                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        className="text-red-600 cursor-pointer"
                                        onClick={() => {
                                            logout();
                                            setShowLogoutModal(true);
                                            setTimeout(() => navigate('/'), 2000);
                                        }}
                                    >
                                        <LogOut className="w-4 h-4 mr-2" />
                                        Sair
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="hover:scale-105 hover:bg-accent hover:text-accent-foreground transition-all duration-300"
                                    >
                                        <User className="w-4 h-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <DropdownMenuItem asChild>
                                        <Link to="/login" className="flex items-center">
                                            <LogIn className="w-4 h-4 mr-2" />
                                            Entrar
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to="/register" className="flex items-center">
                                            <UserPlus className="w-4 h-4 mr-2" />
                                            Criar Conta
                                        </Link>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}

                        {/* Mobile Menu Button */}
                        <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                            <SheetTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="lg:hidden p-2"
                                    aria-label="Abrir menu"
                                >
                                    <Menu className="h-5 w-5" />
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="right" className="w-80 px-6 mobile-menu-center [&>button]:hidden">
                                <div className="flex flex-col h-full">
                                    {/* Mobile Header */}
                                    <div className="flex items-center justify-between py-4 border-b">
                                        <div className="flex items-center gap-1">
                                            <img
                                                src="https://ik.imagekit.io/AlphaLabs/logo.webp?tr=w-180,q-90,f-webp"
                                                alt="AlphaLabs Logo"
                                                className="h-8 w-8 object-contain rounded-lg"
                                            />
                                            <span className="font-bold text-lg bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                                                AlphaLabs
                                            </span>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setIsMobileMenuOpen(false)}
                                            className="p-2"
                                        >
                                            <X className="h-5 w-5" />
                                        </Button>
                                    </div>

                                    {/* Mobile Contact Info */}
                                    <div className="py-4 border-b">
                                        <div className="space-y-2 text-sm text-muted-foreground">
                                            <a
                                                href="tel:+5573988188407"
                                                className="flex items-center gap-3 hover:text-primary transition-colors"
                                            >
                                                <Phone className="w-4 h-4" />
                                                (73) 98818-8407
                                            </a>
                                            <a
                                                href="mailto:contato@alphalabs.dev"
                                                className="flex items-center gap-3 hover:text-primary transition-colors"
                                            >
                                                <Mail className="w-4 h-4" />
                                                alphalabsia@gmail.com
                                            </a>
                                        </div>
                                    </div>

                                    {/* Mobile Navigation */}
                                    <nav className="flex-1 py-6 space-y-2" aria-label="Menu Mobile">
                                        {navigation.map((item) => (
                                            <NavLink
                                                key={item.href}
                                                to={item.href}
                                                className={mobileNavClass}
                                                onClick={() => setIsMobileMenuOpen(false)}
                                                end={item.href === "/"}
                                            >
                                                {item.label}
                                            </NavLink>
                                        ))}
                                    </nav>


                                    {/* Mobile CTA + User Menu */}
                                    <div className="space-y-3 py-6 border-t">
                                        <div className="w-full text-center">
                                            <Link
                                                to="/contato"
                                                onClick={() => setIsMobileMenuOpen(false)}
                                                className="inline-flex items-center justify-center w-full px-4 py-3 border border-primary/30 rounded-lg bg-background text-foreground hover:bg-accent transition-colors"
                                            >
                                                <Mail className="w-4 h-4 mr-2" />
                                                Solicitar Orçamento
                                            </Link>
                                        </div>

                                        {!isAuthenticated && (
                                            <div className="space-y-2">
                                                <div className="w-full text-center">
                                                    <Link
                                                        to="/login"
                                                        onClick={() => setIsMobileMenuOpen(false)}
                                                        className="inline-flex items-center justify-center w-full px-4 py-3 rounded-lg text-foreground hover:bg-accent transition-colors"
                                                    >
                                                        <LogIn className="w-4 h-4 mr-2" />
                                                        Entrar
                                                    </Link>
                                                </div>
                                                {/* ... outros botões */}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </SheetContent>
                        </Sheet>
                    </div>
                </div>
            </div>
        </header>
        <LogoutSuccessModal
                    isOpen={showLogoutModal}
                    onClose={() => setShowLogoutModal(false)}
                />
                </>
    );
};

export default SaaSHeader;